import type { MetaFunction, LoaderFunctionArgs, LinksFunction } from "@remix-run/node";
import { json, redirect, defer } from "@remix-run/node";
import {
  useLoaderData,
  useSearchParams,
} from "@remix-run/react";
import { useEffect, useMemo, useState, useCallback, useRef } from "react";
import {
  DEFAULT_CURRENCY,
  DEFAULT_REGION,
  DEFAULT_REGION_LENGTH,
} from "~/lib/constants/generalConstants";
import { getCountryData, regionConfigs } from "custom-config/region.configs";
import { fetchCurrentLocation, getLocationFromIp } from "~/lib/helpers/googleLocationDataManager";
import { getCountryName } from "~/lib/helpers/constants";

import WorkspaceService from "~/lib/services/WorkspaceService";

import { getDeviceInfo } from "~/lib/hooks/useViewportListener";
import ApiServiceV2 from "~/lib/services/GeoSearchServices";
import DesktopLayout from "~/components/blocks/Home_Page/DesktopLayout";
import MobileLayout from "~/components/blocks/Home_Page/MobileLayout";
import PropertiesService from "~/lib/services/PropertiesService";
import BlogsService from "~/lib/services/BlogsService";
import HomeServices from "~/lib/services/HomeServices";
import AreaServices from "~/lib/services/AreaServices";
import { FAQ_TYPES } from "~/lib/constants/faqConstants";
import { getSearchPathSlugs } from "~/lib/helpers/ipLocationSearchPathHelper";
import { homeMeta } from "~/utils/Meta/homeMeta";
import { locationGeosearchMeta } from "~/utils/Meta/locationGeosearchMeta";
import LocationGeosearchPage from "~/components/blocks/Geosearch/LocationGeosearchPage";
import { loadGeosearchAtPath } from "~/lib/loaders/locationGeosearch.server";
import { convertSpacesToDashes } from "~/lib/helpers/StringHelper";
import {
  buildCountryPropertyPagePath,
  buildLocationUrlWithOfficeTypeFilter,
  officeTypeValuesToSlugParam,
} from "~/components/blocks/Geosearch/locationGeosearch";
import { clearStaleSearchPlaceContextForCityLevelPath } from "~/lib/helpers/searchPlaceContext";
import { resolveCurrentLocationRedirect } from "~/lib/helpers/currentLocationRedirect.server";
// import { checkBotId } from "~/lib/utils/botid";

const categories = [{ option: "All", value: 'All' }, { option: "Serviced Office", value: 'managed' }, { option: "Coworking Space", value: 'coworking' }, { option: "Private Office", value: 'private' }];
const categoriesMobile = [{ option: "All", value: 'All' }, { option: "Serviced Office", value: 'managed' }, { option: "Coworking Space", value: 'coworking' }, { option: "Private Office", value: 'private' }, { value: "shared", option: "Shared Office" }, { value: "enterprise", option: "Enterprise Office" }, { value: "sublet", option: "Sublease Office" }, { value: "conventional", option: "Traditional Office" }, { value: "virtual", option: "Virtual Office (Business Address)" }];

export const meta: MetaFunction = ({ data, location }) => {
  if (!data || !location) return [];
  if ("currentLocationPath" in data && data.currentLocationPath) {
    return locationGeosearchMeta(data, String(data.currentLocationPath));
  }
  return homeMeta({ data, path: location.pathname || '/' });
};

export const links: LinksFunction = () => [

  {
    rel: "stylesheet",
    href: "https://fonts.googleapis.com/css2?family=Montserrat:wght@400;500;600;700&display=swap",
  },
];

export async function loader({ params, request }: LoaderFunctionArgs) {
  // const botCheck = await checkBotId(request as any);

  // if (botCheck.isBot) {
  //   console.warn("Bot detected on home page:", {
  //     country: params?.country,
  //     userAgent: request.headers.get("user-agent"),
  //     ip: request.headers.get("cf-connecting-ip"),
  //   });

  //   throw json(
  //     { error: "Access denied" },
  //     { status: 403 }
  //   );
  // }
  const { isMobile } = getDeviceInfo(request, 768);
  const region = params.country || DEFAULT_REGION;

  const currentLocationTarget = await resolveCurrentLocationRedirect(request, region);
  if (currentLocationTarget) {
    const payload = await loadGeosearchAtPath(request, currentLocationTarget);
    if (payload) {
      return json(
        { ...payload, currentLocationPath: currentLocationTarget },
        { headers: { "Cache-Control": "private, no-store" } },
      );
    }
  }

  let lat = ''
  let lng = ''
  let country = ''

  const cookieHeader = request.headers.get('Cookie');
  if (cookieHeader) {
    const match = cookieHeader.match(/lastSearchedLocation=([^;]+)/);
    if (match) {
      try {
        const lastSearched = JSON.parse(decodeURIComponent(match[1]));
        lat = lastSearched.lat;
        lng = lastSearched.lng;
        country = lastSearched.country

      } catch {
        // ignore invalid cookie
      }
    }
  }




  // Defer ALL API calls for instant page load
  const homeSummaryPromise = await HomeServices.getHomeSummary(region).catch(() => ({
    totalProperties: 0,
    totalhappycustomers: 0,
    sumRatings: '5.0',
    videoUrl: ''
  }));

  let location = await fetchCurrentLocation(request)


  const countryName = getCountryName(region)


  const featuredPlacesPromise = await HomeServices.getFeaturedPlaces(region).catch(() => []);
  const partnersPromise = await WorkspaceService.getWorkspacePartners(countryName, 8).catch(() => []);
  const blogsPromise = await BlogsService.getBlogsByCategory('', getCountryName(region)).catch(() => ({ blogs: [] }));
  const citiesPromise = await HomeServices.getCitiesByRegion(region, 'Office Space').catch(() => ({ results: [] }));
  // const geoData = PropertiesService.getNearByProperties(location?.lat || 0, location?.lon || 0, 20, "nearby", 15, true).catch(() => ({ results: [] }));
  const nearByPropertiesPromise = await PropertiesService.getFeauteredProperties(countryName).catch(() => []);
  const faqsPromise = AreaServices.getFAQsParentPage(FAQ_TYPES.HOME, countryName).catch(() => []);
  const regionData = getCountryData((region || DEFAULT_REGION));

  const userCurrency = regionData?.currency || DEFAULT_CURRENCY;

  if (region.length !== DEFAULT_REGION_LENGTH || !(region in regionConfigs)) {
    throw redirect("/");
  }
  return defer(
    {
      isMobile,
      country: getCountryName(region),
      region,
      userCurrency,
      partners: partnersPromise,
      nearByProperties: nearByPropertiesPromise,
      homeSummary: homeSummaryPromise,
      featuredPlaces: featuredPlacesPromise,
      blogs: blogsPromise,
      cities: citiesPromise,
      faqs: faqsPromise,
      // geoData,
      location,
      regionData
    },
    // {
    //   headers: {
    //     // Cache JSON/API responses for 10 min in browser, 20 min in CDN, stale-while-revalidate for 24 hours
    //     "Cache-Control": "public, max-age=600, s-maxage=1200, stale-while-revalidate=86400",
    //   },
    // }
  );
}

export default function Index() {
  const loaderData = useLoaderData<typeof loader>();
  const currentLocationPath =
    loaderData &&
    typeof loaderData === "object" &&
    "currentLocationPath" in loaderData &&
    typeof loaderData.currentLocationPath === "string"
      ? loaderData.currentLocationPath
      : "";

  if (currentLocationPath) return <LocationGeosearchPage />;
  return <CountryHomePage />;
}

function CountryHomePage() {
  const loaderData = useLoaderData<typeof loader>();

  const { isMobile, region, userCurrency, country, regionData } =
    loaderData as Exclude<typeof loaderData, { currentLocationPath: string }>;


  useSearchParams();
  const [drawer, setDrawer] = useState(false);
  const [query, setQuery] = useState("");
  const [url, setUrl] = useState("");
  const defaultFilters = useMemo(() => ({
    price: [],
    deskRange: [],
    officeTypes: ['All'],
    facilities: [],
    workspaceId: null,
    minPeople: "",
    maxPeople: "",
    minPrice: "",
    maxPrice: "",
  }), []);

  const [filterData, setFilterData] = useState<any>(() => ({ ...defaultFilters }));

  const [userLocation, setUserLocation] = useState<{
    lat?: number;
    lon?: number;
    regionName?: string;
    city?: string;
    countryCode?: string;
    region?: string;
  } | null>(null)
  const [range, setRange] = useState<any>(null);

  const geoDataPromiseRef = useRef<Promise<any> | null>(null);
  const userLocationRef = useRef<{ lat?: number; lon?: number } | null>(null);

  // Get user location for search
  const getUserLocation = useCallback(async () => {
    // Handle deferred location
    const location = await loaderData.location;
    setUserLocation(location);
    if (!location?.countryCode) {
      // Crawlers/inspection tools frequently have no resolvable IP-based location.
      // Avoid crashing client render paths that assume a countryCode.
      setUrl("");
      return;
    }
    const { stateSlug, citySlug } = getSearchPathSlugs(location);
    const countryCode = location.countryCode.toLowerCase() === "gb"
      ? "uk"
      : location.countryCode.toLowerCase();
    const nextUrl =
      stateSlug && citySlug
        ? `/${countryCode}/${stateSlug}/${citySlug}`
        : buildCountryPropertyPagePath(countryCode);
    setUrl(`${window.location.origin}${nextUrl}`);
  }, [loaderData]);

  useEffect(() => {
    getUserLocation()
  }, [loaderData.location])

  useEffect(() => {
    const fetchRange = async () => {
      const rangeData = await ApiServiceV2.getCountryMaxPrice(`?country=${country}`).catch(() => null);
      setRange(rangeData);
    };
    if (country) {
      fetchRange();
    }
  }, [country])

  // Create geoData promise only once when userLocation is available and hasn't changed
  useEffect(() => {
    if (userLocation && (
      !geoDataPromiseRef.current ||
      userLocation.lat !== userLocationRef.current?.lat ||
      userLocation.lon !== userLocationRef.current?.lon
    )) {
      geoDataPromiseRef.current = PropertiesService.getNearByProperties(
        userLocation?.lat || 0,
        userLocation?.lon || 0,
        20,
        "nearby",
        15,
        true
      ).catch(() => ({ results: [] }));
      userLocationRef.current = { lat: userLocation.lat, lon: userLocation.lon };
    } else if (!userLocation) {
      geoDataPromiseRef.current = Promise.resolve({ results: [] });
      userLocationRef.current = null;
    }
  }, [userLocation]);

  const geoDataPromise = useCallback(() => {
    return geoDataPromiseRef.current || Promise.resolve({ results: [] });
  }, []);


  const callGeoSearch = useCallback(
    (options: { [x: string]: string }) => {
      if (options.reset) {
        setFilterData({ ...defaultFilters });
      } else {
        setFilterData((prev: any) => {
          if (options["workspaceType"] !== undefined) {
            const officeTypes = options["workspaceType"]
              .split(",")
              .filter(Boolean);
            return {
              ...prev,
              officeTypes: officeTypes.length ? officeTypes : ['All'],
            };
          }
          return { ...prev, ...options };
        });
      }
    },
    [defaultFilters]
  );

  const callGeoSearchForDrawer = useCallback(
    (options: { [x: string]: string }) => {
      if (options.reset) {
        setFilterData({ ...defaultFilters });
        return;
      }

      setFilterData((prev: any) => {
        if (options.workspaceType !== undefined) {
          const officeTypes = options.workspaceType
            .split(",")
            .filter(Boolean);
          return {
            ...prev,
            officeTypes: officeTypes.length ? officeTypes : ['All'],
          };
        }

        return {
          ...prev,
          ...options,
        };
      });
    },
    [defaultFilters]
  );

  // Memoize query string generation   
  useEffect(() => {
    const queryobj = {
      minPeople: filterData?.minPeople,
      maxPeople: filterData?.maxPeople,
      minPrice: filterData?.minPrice,
      maxPrice: filterData?.maxPrice,
      workspaceType: filterData?.officeTypes?.length
        ? officeTypeValuesToSlugParam(filterData.officeTypes.filter((i: any) => i !== "All"))
        : "",
    };

    const qstr = Object.entries(queryobj)
      .filter(([_, value]) => value)
      .map(([key, value]) => `${key}=${encodeURIComponent(value)}`)
      .join("&");

    setQuery(qstr ? `&${qstr}` : "");
  }, [filterData]);

  let searchUrl = `/${region.toLowerCase()}/${regionData.state.toLowerCase()}/${regionData.city.toLowerCase()}?latitude=${regionData.lat}&longitude=${regionData.lng}&zoom=12`;
  searchUrl = convertSpacesToDashes(searchUrl);

  const finalSearchUrl = useMemo(() => {
    if (!url) return "";
    return buildLocationUrlWithOfficeTypeFilter(
      url,
      query,
      filterData?.officeTypes,
    );
  }, [url, query, filterData?.officeTypes]);

  const handleDrawerApply = useCallback(() => {
    setDrawer(false);
    if (finalSearchUrl) {
      try {
        clearStaleSearchPlaceContextForCityLevelPath(new URL(finalSearchUrl, window.location.origin).pathname);
      } catch {
        // ignore invalid URLs
      }
      window.open(finalSearchUrl, "_self");
    }
  }, [finalSearchUrl]);



  return (
    isMobile ?
      <MobileLayout
        categories={categoriesMobile}
        drawer={drawer}
        setDrawer={setDrawer}
        url={url}
        query={query}
        callGeoSearch={callGeoSearch}
        callGeoSearchForDrawer={callGeoSearchForDrawer}
        onApplyDrawerFilters={handleDrawerApply}
        filterData={filterData}
        setUrl={setUrl}
        loaderData={loaderData}
        region={region}
        countryName={country}
        searchUrl={searchUrl}
        countryCode={loaderData.region}
        state={userLocation?.region}
        city={userLocation?.city}
        initialUserCurrency={userCurrency}
        geoData={geoDataPromise}
        maxPrice={range}
        parsedQuery={{ latitude: userLocation?.lat || "", longitude: userLocation?.lon || "" }}
        regionData={regionData}

      /> :
      <DesktopLayout
        categories={categories}
        drawer={drawer}
        setDrawer={setDrawer}
        url={url}
        query={query}
        callGeoSearch={callGeoSearch}
        callGeoSearchForDrawer={callGeoSearchForDrawer}
        onApplyDrawerFilters={handleDrawerApply}
        filterData={filterData}
        setUrl={setUrl}
        loaderData={loaderData}
        region={region}
        countryName={country}
        searchUrl={searchUrl}
        countryCode={loaderData.region}
        state={userLocation?.region}
        city={userLocation?.city}
        initialUserCurrency={userCurrency}
        geoData={geoDataPromise}
        maxPrice={range}
        parsedQuery={{ latitude: userLocation?.lat || "", longitude: userLocation?.lon || "" }}
        regionData={regionData}
      />
  );
}
